<template>
  <div
    v-if="experiences.length"
    id="experience"
    class="experience-feed"
  >
    <h2
      class="feed-header obs-el"
      data-attr="experience"
    >
      Экскурсии
    </h2>
    <div
      v-if="experiences.length"
      class="feed-list"
    >
      <ExperienceCard
        v-for="(exp, index) in experiences"
        :key="exp.id"
        v-bind="exp"
        :position="index"
        data-test-prefix="feed-item"
        :track-event="cardTrackEvent"
        event-prefix="attractions:sights:sight-popup"
        @set-favorite="handleSetFavorite(exp.id)"
        @set-not-favorite="handleSetNotFavorite(exp.id)"
      />
    </div>
    <div
      v-else
      class="feed-list"
    >
      <ExperienceCardSkeleton
        v-for="i in skeletonCount"
        :key="i"
        :animate="loading"
      />
    </div>
    <div
      v-if="hasMoreExperiences"
      class="feed-actions-wrap"
    >
      <div
        class="feed-actions"
        :class="{ 'is-stuck': isStuck }"
      >
        <UiButton
          class="show-more-button"
          is-full-width
          :variant="UiButtonVariant.PRIMARY"
          :color-mode="UiButtonColorMode.NEUTRAL"
          :text="btnTitle"
          :is-loading="loading"
          @clicked="goTo"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { UiButton, UiButtonVariant, UiButtonColorMode } from '@experience/core-button-ui';
  import { getBootstrapData } from '@experience/core-hostapp-share-lib';
  import { ExperienceCard, ExperienceCardSkeleton } from '@experience/home-experience-card';
  import { sendFavoriteMindboxEvent } from '@experience/mindbox-events';
  import { onMounted, onBeforeUnmount, watch, nextTick, computed, ref, inject, type useShadowRoot } from 'vue';
  import { useRoute } from 'vue-router';

  import { trackEvent } from '@/services/analytics';
  import {
    getExperienceList,
    addExperienceToFavorite,
    removeExperienceFromFavorite,
    getExperiencesFavoritesList,
  } from '@/shared/api/generated/api';
  import type { ExperienceFavoriteItem } from '@/shared/api/generated/api/types.gen';
  import { useOffsetLimitPagination } from '@/shared/helpers/pagination';
  import type { RuntimeExperience, RuntimeExperienceListResponse } from '@/shared/models/api';
  import type { Currency } from '@/shared/models/experience-price';
  import { ExperienceStringTypes, stringToExperienceType } from '@/shared/models/experience-types';
  import { type CityTagsAdvanced } from '@/shared/models/types';
  import { useSightsStore } from '@/shared/store/sights';
  import { useSightAnalyticsParams } from '@/shared/utils/analytics';
  import { useMediaQuery, useScreenSize } from '@/shared/utils/breakpoints';
  import { pluralizeRu } from '@/shared/utils/pluralizeRu';

  const shadowRoot = inject<ReturnType<typeof useShadowRoot>>('shadowRoot', null);

  const cardTrackEvent = trackEvent as unknown as (eventName: string, params?: Record<string, unknown>) => void;

  const rawExperiences = ref<RuntimeExperience[]>([]);
  const favorites = ref<ExperienceFavoriteItem[]>([]);
  const hasMoreExperiences = ref(false);

  // Находит исходную экскурсию по идентификатору карточки.
  const getRawExperience = (experienceId: number) =>
    rawExperiences.value.find((experience) => experience.id === experienceId);

  const { limit, page, offset } = useOffsetLimitPagination(6);
  const loading = ref(true);
  const route = useRoute();
  const slug = route.params.slug as string;
  const sightsStore = useSightsStore();

  const isCustomTablet = useMediaQuery('(min-width: 768px) and (max-width: 1100px)');
  const { isMobile } = useScreenSize();
  const skeletonCount = computed(() => {
    if (isMobile.value) {
      return 1;
    }

    if (isCustomTablet.value) {
      return 2;
    }

    return 6;
  });

  const props = defineProps<{
    sight: CityTagsAdvanced;
  }>();

  const btnTitle = computed(() => {
    const count = props.sight.excursions_count;

    return count
      ? `Показать ${String(count)} ${pluralizeRu(count, 'экскурсию,экскурсии,экскурсий')}`
      : 'Все предложения';
  });

  const isStuck = ref(false);
  let modalScrollEl: HTMLElement | null = null;
  const BOTTOM_THRESHOLD = 100;

  const onModalScroll = () => {
    if (!modalScrollEl) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = modalScrollEl;

    isStuck.value = scrollHeight - scrollTop - clientHeight > BOTTOM_THRESHOLD;
  };

  const initScrollListener = () => {
    if (modalScrollEl) {
      return;
    }

    modalScrollEl =
      shadowRoot?.querySelector<HTMLElement>('.ui-modal__scroll-container') ??
      document.querySelector<HTMLElement>('.ui-modal__scroll-container');

    if (!modalScrollEl) {
      return;
    }

    modalScrollEl.addEventListener('scroll', onModalScroll, { passive: true });
    onModalScroll();
  };

  const experiences = computed(() =>
    rawExperiences.value.map((exp) => {
      const city = exp.geo?.city ? exp.geo.city[0] : undefined;
      const guide = exp.guide;
      const { price } = exp;
      const favorite = favorites.value.findIndex((item) => item.item_id === exp.id) !== -1;
      let experienceType;

      try {
        experienceType = stringToExperienceType(exp.type);
      } catch (error) {
        console.warn('Unsupported experience type', { id: exp.id, type: exp.type, error });
        experienceType = stringToExperienceType(ExperienceStringTypes.GROUP);
      }

      return {
        id: exp.id,
        title: exp.title,
        tagline: exp.tagline,
        experienceType,
        showGuideInfo: Boolean(guide?.first_name),
        guide: {
          url: guide?.url ?? '',
          name: guide?.first_name ?? '',
          avatar: guide?.avatar?.small ?? '',
        },
        link: `/experience/${String(exp.id)}/`,
        reviewsCount: exp.review_count,
        city: city?.name,
        cityLink: city?.slug ? `/experience/${city.slug}/` : '',
        imageLink: exp.photos?.[0]?.thumbnail ?? '',
        imageAltText: exp.title,
        rating: exp.rating,
        price: price?.value,
        priceFrom: price?.price_from ?? false,
        currency: price?.currency as Currency | undefined,
        unit: price?.unit_string,
        discount: price?.discount?.value,
        originalPrice: price?.discount?.original_price,
        maxPersonsRange: price?.max_persons_range,
        discountExpirationText: price?.discount?.expiration_text,
        duration: exp.duration,
        tour: exp.type === ExperienceStringTypes.TOUR,
        movementType: exp.movement_type,
        count: exp.max_persons,
        inactive: exp.status !== 'active',
        maxPersons: exp.max_persons,
        favorite,
        guideId: guide?.id ?? 0,
        type: exp.type,
        format: exp.format,
        instantBooking: exp.instant_booking ?? false,
        paidPromotion: exp.is_paid ?? false,
      };
    }),
  );

  const fetchData = async () => {
    loading.value = true;
    sightsStore.navIsLoading = true;

    let response;
    let favoritesList;

    const tagId = props.sight.tag_id ?? props.sight.id;

    if (!tagId) {
      loading.value = false;
      sightsStore.navIsLoading = false;

      return;
    }

    try {
      [response, favoritesList] = await Promise.all([
        getExperienceList({
          query: {
            limit: limit.value,
            offset: offset.value,
            tags: String(tagId),
            city__slug: slug,
            price_format: 'travelers',
          },
        }),
        getExperiencesFavoritesList(),
      ]);
    } catch (e) {
      console.error(e);
    } finally {
      loading.value = false;
    }

    const data = response?.data as RuntimeExperienceListResponse | undefined;

    sightsStore.modalNav.experience = !!data?.results?.length;

    rawExperiences.value = rawExperiences.value.concat(data?.results ?? []);
    favorites.value = favoritesList?.data ?? [];
    hasMoreExperiences.value = Boolean(data?.next);
    page.value += 1;
    loading.value = false;
    sightsStore.navIsLoading = false;
  };

  const analyticsParams = useSightAnalyticsParams(() => props.sight);

  const goTo = () => {
    void trackEvent('attractions:sights:sight-popup:show-all-experiences.click', {
      experience_count: props.sight.excursions_count,
      ...analyticsParams.value,
    });
    window.open(`/experience/${slug}/${props.sight.slug ?? ''}`, '_blank');
  };

  onMounted(() => {
    void fetchData();
  });

  watch(
    () => experiences.value.length,
    async (len) => {
      if (len > 0) {
        await nextTick();
        initScrollListener();
      }
    },
  );

  onBeforeUnmount(() => {
    modalScrollEl?.removeEventListener('scroll', onModalScroll);
  });

  // Флаг предотвращает одновременное изменение избранного.
  let favoritesToggling = false;

  // Добавляет экскурсию в избранное и фиксирует исходное действие до API.
  const handleSetFavorite = async (experienceId: number) => {
    if (favoritesToggling) {
      return;
    }

    let favoriteItem;
    const experience = getRawExperience(experienceId);

    favoritesToggling = true;

    try {
      if (experience) {
        sendFavoriteMindboxEvent({
          action: 'add',
          bootstrapData: getBootstrapData(),
          experience,
        });
      }

      favoriteItem = await addExperienceToFavorite({ path: { experienceId }, throwOnError: true });
    } catch (e) {
      favoritesToggling = false;

      throw e;
    }

    const data = favoriteItem.data as ExperienceFavoriteItem | undefined;

    if (data) {
      favorites.value = [...favorites.value, data];
    }

    favoritesToggling = false;
  };

  // Удаляет экскурсию из избранного и фиксирует исходное действие до API.
  const handleSetNotFavorite = async (experienceId: number) => {
    if (favoritesToggling) {
      return;
    }

    const experience = getRawExperience(experienceId);

    favoritesToggling = true;

    try {
      if (experience) {
        sendFavoriteMindboxEvent({
          action: 'remove',
          bootstrapData: getBootstrapData(),
          experience,
        });
      }

      await removeExperienceFromFavorite({ path: { experienceId } });
    } catch (e) {
      favoritesToggling = false;

      throw e;
    }

    favorites.value = favorites.value.filter((item) => item.item_id !== experienceId);
    favoritesToggling = false;
  };
</script>

<style lang="scss" scoped>
  @use '@experience/design-tokens/typography' as legacy-typography;
  @use '@experience/shared/dist/assets/adaptivity' as legacy-adaptivity;

  .experience-feed {
    width: 100%;
    margin-top: var(--alias-space-48);
  }

  .feed-header {
    @include legacy-typography.base-600-500-extra;
  }

  .feed-list {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--alias-space-16);
    margin-top: var(--alias-space-16);

    @include legacy-adaptivity.mobile {
      grid-template-columns: 1fr;
      row-gap: var(--alias-space-24);
    }
  }

  .feed-actions-wrap {
    min-height: 88px;
    padding-top: var(--alias-space-16);
  }

  .feed-actions {
    &.is-stuck {
      position: fixed;
      bottom: 0;
      left: 50%;
      z-index: 10;
      display: flex;
      justify-content: center;
      width: var(--modal-wrapper-width, 100%);
      padding: var(--alias-space-16);
      background: linear-gradient(180deg, rgb(255 255 255 / 0%) 0%, rgb(255 255 255) 100%);
      transform: translateX(-50%);
    }

    @include legacy-adaptivity.tablet {
      justify-content: stretch;
    }
  }

  .show-more-button {
    width: 100%;

    .feed-actions.is-stuck & {
      width: auto;
      min-width: 280px;
    }
  }
</style>
